import * as z from 'zod';

// GL accounts grouped by accounting-equation position and normal
// balance side. See specs/ledger-postings/ledger-postings.md.

export const AssetGLAccount = z.enum([
  'principal',
  'principalReceivable',
  'interestReceivable',
  'escrowReceivable',
  // Single bucket — per-type rollups come from joining `feeId`
  // against `Fee.feeType` at report time.
  'feeReceivable',
  'advancePrincipal',
  'advanceInterest',
  // Formal-Advance asset for an implicit escrow shortfall that the
  // lender has adopted as an `Advance{escrow}` (e.g. at
  // payment-deferral activation). Promotion credits `escrow` for the
  // promoted amount, absorbing the negative-escrow position; the
  // legacy mirror is `SnapshotCalculator.getEscrowContributionFromAdvance`.
  'advanceEscrow',
  'deferredPrincipal',
  'deferredInterest',
  'deferredFee',
  'deferredEscrow',
  'delinquentInterestReceivable',
  // Custodial cash held in trust for this loan. Boarding seeds it
  // with the sum of liabilities-in-custody. Servicer-side cash
  // movements use `servicingClearing` instead.
  //
  // Allowed negative: when escrow disbursements exceed available
  // custody (servicer-fronted), the journal records the position
  // inline as negative cash. Presentation-layer reclassification
  // to a payable is handled outside the ledger (ASC 210-20 /
  // 405-20 book-overdraft treatment).
  //
  // @migrationTodo Fannie A4-1-02 forbids commingling P&I and T&I in
  // the same custodial account, but today `cash` is a single bucket.
  'cash',
  // Servicer-side clearing account. Borrower-funded advance payments
  // land here and are immediately CRed by `advanceReimbursement`.
  'servicingClearing',
  // Parks the unrecovered principal between a `waive-suspense`
  // payoff and the writeoff that recognizes the loss. Modeled
  // separately so `suspense` (real cash awaiting allocation) isn't
  // confused with forgiven principal.
  'payoffShortfall',
]);
export type AssetGLAccount = z.infer<typeof AssetGLAccount>;

export const LiabilityGLAccount = z.enum([
  'escrow',
  'suspense',
  'futureStatementSuspense',
  'unappliedSuspense',
  // Chapter 13 pre-petition plan funds received from the trustee/borrower and held until
  // reallocated onto pre-petition statements/charges. A suspense-family bucket (see
  // SuspenseGLAccount), earmarked so pre-petition arrears cures don't commingle with
  // ordinary unapplied cash.
  'prePetitionUnappliedSuspense',
  'holdback',
  'reserve',
  // Counterpart to `principal` at boarding. Stays put as the
  // borrower pays down principal — the lender's capital obligation
  // is independent of borrower pay-down. Drawn down by future
  // investor-payoff / servicing-release postings.
  'noteHolder',
  // Counterpart to advance assets. Shrinks via `advanceReimbursement`
  // when the servicer recovers from loan cash.
  'advanceFunding',
  // Placeholder paired with `escrowReceivable` at assessment. Real
  // `escrow` doesn't move at billing time. Converts to `escrow` on
  // payment, or pairs out against the receivable on cancellation /
  // payoff. Mirrors `advanceFunding`'s pair-in/pair-out shape.
  'escrowObligation',
]);
export type LiabilityGLAccount = z.infer<typeof LiabilityGLAccount>;

export const RevenueGLAccount = z.enum(['interest', 'feeRevenue', 'delinquentInterest']);
export type RevenueGLAccount = z.infer<typeof RevenueGLAccount>;

// Expense / loss (debit-normal). `chargeoff` is the GAAP / Fannie
// F-1-05 / ASC 326 name (the legacy `WriteoffId` source-object name
// is unchanged). `transferredFunds` flows borrower money forwarded to
// the next servicer mid-cycle. There's no standalone `disbursement`
// expense — each disbursement posting kind debits the source liability
// directly (matching custodial semantics).
export const ExpenseGLAccount = z.enum(['chargeoff', 'modification', 'transferredFunds']);
export type ExpenseGLAccount = z.infer<typeof ExpenseGLAccount>;

export const DebitNormalGLAccount = z.enum([...AssetGLAccount.options, ...ExpenseGLAccount.options]);
export type DebitNormalGLAccount = z.infer<typeof DebitNormalGLAccount>;

export const CreditNormalGLAccount = z.enum([...LiabilityGLAccount.options, ...RevenueGLAccount.options]);
export type CreditNormalGLAccount = z.infer<typeof CreditNormalGLAccount>;

export const GLAccount = z.enum([...DebitNormalGLAccount.options, ...CreditNormalGLAccount.options]);
export type GLAccount = z.infer<typeof GLAccount>;

// Statement receivables → paymentDueId; fee receivables → feeId.
export const StatementReceivableGLAccount = z.enum([
  'principalReceivable',
  'interestReceivable',
  'escrowReceivable',
  'delinquentInterestReceivable',
]);
export type StatementReceivableGLAccount = z.infer<typeof StatementReceivableGLAccount>;

export const FeeReceivableGLAccount = z.enum(['feeReceivable']);
export type FeeReceivableGLAccount = z.infer<typeof FeeReceivableGLAccount>;

export const ReceivableGLAccount = z.enum([...StatementReceivableGLAccount.options, ...FeeReceivableGLAccount.options]);
export type ReceivableGLAccount = z.infer<typeof ReceivableGLAccount>;

export const FeeRevenueGLAccount = z.enum(['feeRevenue']);
export type FeeRevenueGLAccount = z.infer<typeof FeeRevenueGLAccount>;

export const SuspenseGLAccount = z.enum([
  'suspense',
  'futureStatementSuspense',
  'unappliedSuspense',
  'prePetitionUnappliedSuspense',
]);
export type SuspenseGLAccount = z.infer<typeof SuspenseGLAccount>;

export const DeferredGLAccount = z.enum(['deferredPrincipal', 'deferredInterest', 'deferredFee', 'deferredEscrow']);
export type DeferredGLAccount = z.infer<typeof DeferredGLAccount>;

export const AdvanceGLAccount = z.enum(['advancePrincipal', 'advanceInterest', 'advanceEscrow']);
export type AdvanceGLAccount = z.infer<typeof AdvanceGLAccount>;
